import { CloseIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { Button } from '@components/Button'
import LocationAutocomplete from '@components/LocationAutocomplete'
import PlaceTypeIcon from '@components/PlaceTypeIcon'
import { PlaceDraft, placeName, placeSecondaryText, placeTextProblem, placeTyped } from '@lib/place'
import { PlaceSearchContext } from '@lib/placeSearch'
import { TextLinkButton } from '@pages/Trips/CreateTripFlow/shared.styles'
// The places search on the design-system field, as the create-trip Where step dresses it.
import { AddField as PlaceSearch } from '@pages/Trips/CreateTripFlow/WhereStep.styles'
import React, { useState } from 'react'
import {
	PlaceAddress,
	PlaceCard,
	PlaceName,
	PlaceSearchRow,
	PlaceText,
	PlaceTile,
} from './EventCreateFlow.styles'

interface PlaceFieldProps {
	/** The search input's id, for the group's label. */
	id: string
	place: PlaceDraft | null
	onPick: (place: PlaceDraft) => void
	onClear: () => void
	/** What the search knows of the trip: its places, the day's destination and that destination's Suggestions. */
	context: PlaceSearchContext
}

/**
 * The What step's place: the places search while there is none — a Google
 * match, or the search's "use it as typed" row for somewhere it doesn't know —
 * and once picked, a card with its type's icon, its name and address, a
 * Change that searches again and a ✕ that clears it.
 */
const PlaceField: React.FC<PlaceFieldProps> = ({ id, place, onPick, onClear, context }) => {
	const [isSearching, setIsSearching] = useState(false)
	const [query, setQuery] = useState('')

	const stopSearching = () => {
		setIsSearching(false)
		setQuery('')
	}

	const pick = (picked: PlaceDraft) => {
		onPick(picked)
		stopSearching()
	}

	if (place && !isSearching) {
		const name = placeName(place)
		const address = placeSecondaryText(place)
		return (
			<PlaceCard>
				<PlaceTile aria-hidden>
					<PlaceTypeIcon types={place.googlePlace?.types} sx={{ fontSize: iconSizes.lg }} />
				</PlaceTile>
				<PlaceText>
					<PlaceName>{name}</PlaceName>
					{address && <PlaceAddress>{address}</PlaceAddress>}
				</PlaceText>
				<Button type="button" variant="ghost" size="sm" aria-label={`Change place, ${name}`} onClick={() => setIsSearching(true)}>
					Change
				</Button>
				<Button type="button" variant="icon" aria-label="Clear place" onClick={onClear}>
					<CloseIcon sx={{ fontSize: iconSizes.md }} />
				</Button>
			</PlaceCard>
		)
	}

	return (
		<PlaceSearchRow
			onKeyDown={e => {
				if (e.key === 'Escape' && place) stopSearching()
			}}
			// Left with nothing typed, a Change has changed nothing: the card comes
			// back, rather than a blank field that still holds the place.
			onBlur={e => {
				if (place && !query.trim() && !e.currentTarget.contains(e.relatedTarget)) stopSearching()
			}}
		>
			<PlaceSearch>
				<LocationAutocomplete
					id={id}
					ariaLabel="Place"
					value={query}
					onChange={setQuery}
					onPlaceSelect={pick}
					onFreeTextSelect={text => {
						if (placeTextProblem(text) === null) pick(placeTyped(text.trim()))
					}}
					placeholder="Search for a place"
					context={context}
					autoFocus={isSearching}
				/>
			</PlaceSearch>
			{place && (
				<TextLinkButton type="button" onClick={stopSearching}>
					Cancel
				</TextLinkButton>
			)}
		</PlaceSearchRow>
	)
}

export default PlaceField
